// Pantalla aparte para gestionar los datos fiscales de un profesor.
// Datos sensibles: IBAN, NIF, razón social, domicilio, régimen, IRPF, notas internas.
// RLS estricta solo admin (la guard de /admin ya lo asegura).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2, Check, ShieldCheck } from 'lucide-react';

import { getProfesorDatosFiscales, putProfesorDatosFiscales } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const FORM_VACIO = {
  iban: '',
  nif: '',
  razon_social: '',
  domicilio_fiscal: '',
  regimen_fiscal: 'autonomo_facturador',
  tipo_irpf: '',
  notas_internas_admin: '',
};

const REGIMENES = [
  { value: 'autonomo_facturador', labelKey: 'admin.profesores.fiscal.regimen.autonomo_facturador' },
  { value: 'con_irpf', labelKey: 'admin.profesores.fiscal.regimen.con_irpf' },
  { value: 'sin_retencion', labelKey: 'admin.profesores.fiscal.regimen.sin_retencion' },
];

export default function AdminProfesorDatosFiscales() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [profesor, setProfesor] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [original, setOriginal] = useState(FORM_VACIO);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProfesorDatosFiscales(id)
      .then((d) => {
        if (cancelled) return;
        setProfesor(d.profesor);
        if (d.datos_fiscales) {
          const f = {
            iban: d.datos_fiscales.iban || '',
            nif: d.datos_fiscales.nif || '',
            razon_social: d.datos_fiscales.razon_social || '',
            domicilio_fiscal: d.datos_fiscales.domicilio_fiscal || '',
            regimen_fiscal: d.datos_fiscales.regimen_fiscal || 'autonomo_facturador',
            tipo_irpf: d.datos_fiscales.tipo_irpf == null ? '' : String(d.datos_fiscales.tipo_irpf),
            notas_internas_admin: d.datos_fiscales.notas_internas_admin || '',
          };
          setForm(f);
          setOriginal(f);
        }
      })
      .catch((e) => { if (!cancelled) setLoadError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(original);

  const submit = async (e) => {
    e.preventDefault();
    setOkMsg(null); setErrMsg(null);
    // Confirmación extra al guardar — datos sensibles
    const confirmar = window.confirm(t('admin.profesores.fiscal.confirmar_guardar'));
    if (!confirmar) return;

    setSaving(true);
    try {
      const payload = {
        iban: form.iban.trim() || null,
        nif: form.nif.trim() || null,
        razon_social: form.razon_social.trim() || null,
        domicilio_fiscal: form.domicilio_fiscal.trim() || null,
        regimen_fiscal: form.regimen_fiscal,
        tipo_irpf: form.tipo_irpf === '' ? null : Number(form.tipo_irpf),
        notas_internas_admin: form.notas_internas_admin.trim() || null,
      };
      const resp = await putProfesorDatosFiscales(id, payload);
      const d = resp.datos_fiscales;
      const updatedForm = {
        iban: d.iban || '',
        nif: d.nif || '',
        razon_social: d.razon_social || '',
        domicilio_fiscal: d.domicilio_fiscal || '',
        regimen_fiscal: d.regimen_fiscal || 'autonomo_facturador',
        tipo_irpf: d.tipo_irpf == null ? '' : String(d.tipo_irpf),
        notas_internas_admin: d.notas_internas_admin || '',
      };
      setForm(updatedForm);
      setOriginal(updatedForm);
      setOkMsg(t('admin.profesores.fiscal.guardado.' + (resp.accion || 'actualizada')));
      setTimeout(() => setOkMsg(null), 4000);
    } catch (e) {
      const msg = e instanceof ApiError ? (e.message || t('admin.profesores.fiscal.error_guardar'))
                                        : t('admin.profesores.fiscal.error_guardar');
      setErrMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>;
  }
  if (loadError) {
    return (
      <div className="admin-page">
        <button type="button" className="profesor-volver" onClick={() => navigate('/admin/profesores')}>
          <ArrowLeft size={16} strokeWidth={1.5} /> {t('admin.profesores.fiscal.volver')}
        </button>
        <div className="admin-error"><AlertCircle size={20} /><span>{loadError.message}</span></div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-mi-cuenta">
      <button type="button" className="profesor-volver" onClick={() => navigate('/admin/profesores')}>
        <ArrowLeft size={16} strokeWidth={1.5} /> {t('admin.profesores.fiscal.volver')}
      </button>

      <header className="admin-page-head">
        <h1>
          <ShieldCheck size={20} strokeWidth={1.5} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          {t('admin.profesores.fiscal.titulo')}
        </h1>
        <p className="admin-page-sub">
          {profesor?.nombre_publico} · <em>{t('admin.profesores.fiscal.subtitulo_aviso')}</em>
        </p>
      </header>

      <form onSubmit={submit} className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.profesores.fiscal.bloque_bancarios')}</h2>
        <div className="admin-form-grid">
          <div className="modal-campo">
            <label>{t('admin.profesores.fiscal.f.iban')}</label>
            <input type="text" value={form.iban} onChange={setCampo('iban')} placeholder="ES91 2100 0418 4502 0005 1332" />
          </div>
          <div className="modal-campo">
            <label>{t('admin.profesores.fiscal.f.nif')}</label>
            <input type="text" value={form.nif} onChange={setCampo('nif')} placeholder="12345678Z" />
          </div>
          <div className="modal-campo full">
            <label>{t('admin.profesores.fiscal.f.razon_social')}</label>
            <input type="text" value={form.razon_social} onChange={setCampo('razon_social')} />
          </div>
          <div className="modal-campo full">
            <label>{t('admin.profesores.fiscal.f.domicilio_fiscal')}</label>
            <input type="text" value={form.domicilio_fiscal} onChange={setCampo('domicilio_fiscal')} maxLength={200} />
          </div>
        </div>

        <h2 className="admin-card-titulo" style={{ marginTop: 28 }}>{t('admin.profesores.fiscal.bloque_regimen')}</h2>
        <div className="admin-form-grid">
          <div className="modal-campo full">
            <label>{t('admin.profesores.fiscal.f.regimen_fiscal')}</label>
            <div className="admin-radios">
              {REGIMENES.map((r) => (
                <label key={r.value} className="admin-radio">
                  <input
                    type="radio"
                    name="regimen_fiscal"
                    value={r.value}
                    checked={form.regimen_fiscal === r.value}
                    onChange={setCampo('regimen_fiscal')}
                  />
                  <span>{t(r.labelKey)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="modal-campo">
            <label>{t('admin.profesores.fiscal.f.tipo_irpf')}</label>
            <input
              type="number" min="0" max="50" step="0.01"
              value={form.tipo_irpf}
              onChange={setCampo('tipo_irpf')}
              placeholder="0 - 50"
              disabled={form.regimen_fiscal !== 'con_irpf'}
            />
            {form.regimen_fiscal !== 'con_irpf' && (
              <small className="admin-hint">{t('admin.profesores.fiscal.f.tipo_irpf_solo_con_irpf')}</small>
            )}
          </div>
        </div>

        <h2 className="admin-card-titulo" style={{ marginTop: 28 }}>{t('admin.profesores.fiscal.bloque_notas')}</h2>
        <div className="admin-form-grid">
          <div className="modal-campo full">
            <label>{t('admin.profesores.fiscal.f.notas_internas_admin')}</label>
            <textarea rows={3} value={form.notas_internas_admin} onChange={setCampo('notas_internas_admin')}
                      placeholder={t('admin.profesores.fiscal.f.notas_placeholder')} />
          </div>
        </div>

        {okMsg && (
          <div className="admin-msg-ok">
            <Check size={14} strokeWidth={1.75} /> {okMsg}
          </div>
        )}
        {errMsg && (
          <div className="modal-error-general">
            <AlertCircle size={14} strokeWidth={1.75} /> {errMsg}
          </div>
        )}

        <div className="admin-form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/profesores')} disabled={saving}>
            {t('comun.cancelar')}
          </button>
          <button type="submit" className="btn-primary" disabled={saving || !dirty}>
            {saving ? <><Loader2 size={14} className="spin" /> {t('admin.profesores.fiscal.guardando')}</> : t('admin.profesores.fiscal.guardar')}
          </button>
        </div>
      </form>
    </div>
  );
}
